'use client';

import { type FragmentType, getFragmentData } from '@packages/gql/generated/shopping';
import { type ImageGalleryColorDialogFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { ImageGalleryColorDialogFragmentFragmentDoc } from '@packages/gql/generated/shopping/ImageGalleryColorDialogFragmentFragmentDoc';
import { useVariationFormattedLink } from '@packages/modules/src/VariationSelect/utils/useVariationFormattedLink';
import { CloseIconButton } from '@packages/shared/src/components/CloseIconButton/CloseIconButton';
import { Modal } from '@packages/shared/src/components/Modal/Modal';
import { Slide } from '@packages/shared/src/components/Slide/Slide';
import { ColorPicker32 } from '@packages/themes/icons';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { HorizontalScroll } from '../../HorizontalScroll';
import { ImageGalleryCallToActionButton } from '../../ImageGalleryCallToActionButton';
import { ImageGalleryColorDialogItem } from '../ImageGalleryColorDialogItem/ImageGalleryColorDialogItem';
import { imageGalleryColorDialogMessages } from './messages';

/* GraphQL */ `
  fragment ImageGalleryColorDialogFragment on Product {
    dimensions {
      axisType
      values {
        sku
        variationMainImage
        isSelected
        rgb
        displayText
      }
    }
  }
`;

export interface ImageGalleryColorDialogProps {
  /**
   * Target (COLOR) dimension of the product
   */
  data: FragmentType<typeof ImageGalleryColorDialogFragmentFragmentDoc>;
}

export const ImageGalleryColorDialog = ({ data }: ImageGalleryColorDialogProps) => {
  const { dimensions } = getFragmentData(ImageGalleryColorDialogFragmentFragmentDoc, data);
  const [openColorDialog, setOpenColorDialog] = useState(false);
  const { formatMessage } = useIntl();
  const dispatch = useTracking();
  const { push } = useRouter();
  const createVariationLink = useVariationFormattedLink();

  useEffect(() => {
    if (openColorDialog) {
      window.scroll({ behavior: 'smooth', left: 0, top: 0 });
    }
  }, [openColorDialog]);

  const handleColorPickerClick = () => {
    dispatch({
      event: 'ga_event',
      eventAction: 'rainbowpicker',
      eventCategory: 'userInteraction',
      eventLabel: 'rainbowpicker',
      eventValue: 'open',
    });
    setOpenColorDialog(true);
  };

  const dimension = dimensions.find(({ axisType }) => axisType === 'COLOR');

  if (!dimension) {
    return null;
  }
  const modalCloseHandler = () => {
    dispatch({
      event: 'ga_event',
      eventAction: 'rainbowpicker',
      eventCategory: 'userInteraction',
      eventLabel: 'rainbowpicker',
      eventValue: 'close',
    });
    setOpenColorDialog(false);
  };

  const onSelect = (
    sku: ImageGalleryColorDialogFragmentFragment['dimensions'][number]['values'][number]['sku'],
  ) => {
    setTimeout(() => setOpenColorDialog(false), 400);

    if (sku) {
      push(createVariationLink(sku));

      return setTimeout(() => {
        dispatch({
          event: 'ga_event',
          eventAction: 'rainbowpicker',
          eventCategory: 'userInteraction',
          eventLabel: 'rainbowpicker',
          eventValue: 'variationChange',
        });
      }, 550);
    }

    return dispatch({
      event: 'ga_event',
      eventAction: 'rainbowpicker',
      eventCategory: 'userInteraction',
      eventLabel: 'rainbowpicker',
      eventValue: 'close',
    });
  };

  return (
    <>
      <ImageGalleryCallToActionButton
        onClick={handleColorPickerClick}
        buttonAriaLabel={formatMessage(imageGalleryColorDialogMessages.buttonName)}
      >
        <ColorPicker32 />
      </ImageGalleryCallToActionButton>
      {openColorDialog && (
        <Modal open={openColorDialog} onClose={modalCloseHandler}>
          <Slide in={openColorDialog} direction="up">
            <div className="bg-color-text-light fixed bottom-0 w-full px-2">
              <div className="my-2.5 flex items-center justify-between gap-1">
                <p className="text-size-body2">
                  {formatMessage(imageGalleryColorDialogMessages.selectColor)}
                </p>
                <CloseIconButton onClick={modalCloseHandler} />
              </div>
              <div className="mb-3">
                <HorizontalScroll
                  sx={{ alignItems: 'stretch' }}
                  gap={1.5}
                  scrollToItemIndex={dimension.values.findIndex(({ isSelected }) => isSelected)}
                >
                  {dimension.values.map((value) => (
                    <ImageGalleryColorDialogItem
                      key={value.variationMainImage ?? value.sku}
                      value={value}
                      onSelect={() => onSelect(value.sku)}
                    />
                  ))}
                </HorizontalScroll>
              </div>
            </div>
          </Slide>
        </Modal>
      )}
    </>
  );
};
